import type { components } from '@/api/schema';
import { AppIcon, type IconKind } from '@/components/AppIcon';

type HierarchyFarm = components['schemas']['HierarchyFarmDto'];
type HierarchyShed = components['schemas']['HierarchyShedDto'];
type HierarchySection = components['schemas']['HierarchySectionDto'];

/**
 * The org-chart view of Farm → Shed → Section → Pen, drawn from data the Farm
 * Manager already holds. Structural counts only — no bird or mortality
 * figures until Flocks & Grading exists to supply them. A node with children
 * is drawn in the accent style and an empty one stays plain, which reads
 * correctly without those figures.
 *
 * Clicking a node reports the navigator id it corresponds to; the Farm
 * Manager selects it and switches back to the grid. A pen reports its section,
 * since pens are listed in their section's grid rather than being tree nodes.
 */
export function FarmHierarchyTree({
  farms,
  onSelect,
}: {
  farms: HierarchyFarm[];
  onSelect: (nodeId: string) => void;
}) {
  const totalSheds = farms.reduce((n, f) => n + f.sheds.length, 0);

  return (
    <nav className="farmtree" aria-label="Farm chart">
      <ul>
        <li>
          <TreeNode
            kind="allFarms"
            variant="root"
            title="All farms"
            lines={[plural(farms.length, 'farm'), plural(totalSheds, 'shed')]}
            onClick={() => onSelect('all')}
          />
          {farms.length > 0 && (
            <ul>
              {farms.map((farm) => (
                <li key={farm.id}>
                  <TreeNode
                    kind="farm"
                    variant={farm.sheds.length > 0 ? 'accent' : 'plain'}
                    title={farm.nameEn}
                    lines={[farm.isActive ? plural(farm.sheds.length, 'shed') : 'Deleted']}
                    onClick={() => onSelect(`farm:${farm.id}`)}
                  />
                  {farm.sheds.length > 0 && (
                    <ul>
                      {farm.sheds.map((shed) => (
                        <ShedNode key={shed.id} shed={shed} onSelect={onSelect} />
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </li>
      </ul>
    </nav>
  );
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

function ShedNode({ shed, onSelect }: { shed: HierarchyShed; onSelect: (nodeId: string) => void }) {
  return (
    <li>
      <TreeNode
        kind="shed"
        variant={shed.sections.length > 0 ? 'accent' : 'plain'}
        title={shed.nameEn}
        lines={[shed.isActive ? plural(shed.sections.length, 'section') : 'Deleted']}
        onClick={() => onSelect(`shed:${shed.id}`)}
      />
      {shed.sections.length > 0 && (
        <ul>
          {shed.sections.map((section) => (
            <SectionNode key={section.id} section={section} onSelect={onSelect} />
          ))}
        </ul>
      )}
    </li>
  );
}

function SectionNode({
  section,
  onSelect,
}: {
  section: HierarchySection;
  onSelect: (nodeId: string) => void;
}) {
  const open = () => onSelect(`section:${section.id}`);
  return (
    <li>
      <TreeNode
        kind="section"
        variant={section.pens.length > 0 ? 'accent' : 'plain'}
        title={section.nameEn}
        lines={[section.isActive ? plural(section.pens.length, 'pen') : 'Deleted']}
        onClick={open}
      />
      {section.pens.length > 0 && (
        <ul>
          {section.pens.map((pen) => (
            <li key={pen.id}>
              <TreeNode
                kind="pen"
                variant="plain"
                title={pen.nameEn}
                lines={[pen.isActive ? 'Active' : 'Deleted']}
                onClick={open}
              />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function TreeNode({
  kind,
  variant,
  title,
  lines,
  onClick,
}: {
  kind: IconKind;
  variant: 'root' | 'accent' | 'plain';
  title: string;
  lines: string[];
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`farmtree__node farmtree__node--${variant} farmtree__node--link`}
      onClick={onClick}
    >
      <span className="farmtree__title withicon">
        <AppIcon kind={kind} />
        {title}
      </span>
      {lines.map((line) => (
        <span key={line} className="farmtree__line">
          {line}
        </span>
      ))}
    </button>
  );
}
